<script lang="ts" setup>
import { computed, nextTick, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import OrderBlock from "@/views/trips/edit/components/OrderBlock.vue";
import TravelOrderBlock from "@/views/trips/edit/components/TravelOrderBlock.vue";
import RouteBlock from "@/views/trips/edit/components/RouteBlock.vue";
import RouteStopBlock from "@/views/trips/edit/components/RouteStopBlock.vue";
import TripStopBlock from "@/views/trips/edit/components/TripStopBlock.vue";
import TripInfo from "@/views/trips/edit/components/TripInfo.vue";
import TooltipButton from "@/components/buttons/TooltipButton.vue";
import ReorderTripStopsDialog from "@/views/trips/edit/components/dialogs/ReorderTripStopsDialog.vue";
import { TRUCK_ID } from "@/constants/query";
import { useSnackbarStore } from "@/store/snackbar.store";
import TripManager from "@/views/trips/edit/services/tripManager/tripManager";
import { TripGrid } from "@/views/trips/edit/services/tripGrid";
import { useError } from "@/composables/useError";
import { useDisableRules } from "@/composables/reactive/useDisableRules";
import AddOrderDialog from "@/views/trips/edit/components/dialogs/AddOrderDialog.vue";
import { API } from "@/api/api";
import type { Trip, TripOrder, TripRoute, TripRouteFreight, TripRouteStop, TripStop } from "@/types/trip";
import type { TravelOrderDto, TripDto } from "@/api/trips/dto/trip.dto";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import AddReloadStopDialog from "@/views/trips/edit/components/dialogs/AddReloadStopDialog.vue";

const { showAPIError } = useError();
const { createTripDispatchDisableRules } = useDisableRules();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const snackbarStore = useSnackbarStore();
const api = new API();

const addOrderDialogVisible = ref(false);
const addReloadStopDialogVisible = ref(false);
const reloadStopPosition = ref(1);

const tripManager = ref<TripManager | null>(null);
const tripGrid = ref<TripGrid | null>(null);
const tripFromAPI = ref<Trip | null>(null);
const loading = ref(false);
const saveLoading = ref(false);
const isReorderStopDialog = ref(false);
const newAddedOrder = ref<TripOrder | null>(null);

const trip = computed<Trip>(() => {
  return tripManager.value?.trip ?? { orders: [], routes: [], tripStops: [] };
});

const orderFreights = computed(() => {
  return trip.value.orders.flatMap((stop) => stop.freights);
});

const cancelEnabled = computed(() => {
  return route.params.id && route.params.id !== "new";
});

const gridStyle = computed(() => {
  return tripGrid.value?.gridStyle ?? {};
});

const stopsContainerStyle = computed(() => {
  return tripGrid.value?.stopsContainerStyle ?? {};
});

const travelOrdersByOrder = computed(() => {
  return tripManager.value?.travelOrdersByOrder ?? {};
});

const saveAndDispatchRules = computed(() => {
  return createTripDispatchDisableRules(tripManager.value?.routes ?? [], true);
});

const sortedTripStops = computed(() => {
  return [...trip.value.tripStops].sort((a, b) => a.position - b.position);
});

function addOrder(order: TripOrder) {
  newAddedOrder.value = order;
  addOrderDialogVisible.value = false;
  tripManager.value?.addOrderToTrip(order);
  showReorderStopsDialog();
}

function removeOrder(order: TripOrder) {
  tripManager.value?.removeOrder(order);
}

function showReloadStopDialog(index: number) {
  addReloadStopDialogVisible.value = true;
  reloadStopPosition.value = index + 2;
}

function addReloadStop(reloadStop: TripStop) {
  tripManager.value?.addReloadStop({
    ...reloadStop,
    position: reloadStopPosition.value,
  });
  addReloadStopDialogVisible.value = false;
}

async function save() {
  if (!tripManager.value) return;

  const id = route.params.id !== "new" ? Number(route.params.id) : undefined;
  const data = tripManager.value.prepareTripForAPI(trip.value, id);

  saveLoading.value = true;
  const resp = await api.trips.upsertTrip(data);

  if (resp.success) {
    toView(resp.data);
    snackbarStore.showSnackbarSuccess(t("general.saved"));
  } else {
    showAPIError(resp, t("general.save-error"));
  }

  saveLoading.value = false;
}

function setTruck(routeItem: TripRoute, truck: TruckDto | null) {
  if (truck) {
    routeItem.truck = truck;
    routeItem.truckId = truck.id;
  } else {
    routeItem.truck = undefined;
    routeItem.truckId = undefined;
  }
}

function toView(updatedTrip: TripDto | null = null) {
  if (!updatedTrip) {
    router.push({
      name: "trip-view",
      params: { id: route.params.id },
    });
    return;
  }

  const truckIds = updatedTrip.routes.flatMap((r) => r.truckId);

  router.replace({
    name: "trip-view",
    params: { id: updatedTrip.id || route.params.id },
    query: { [TRUCK_ID]: truckIds },
  });
}

function addEmptyRoute() {
  tripManager.value?.addRoute(undefined);
}

function changeRoute(routeItem: TripRoute, stop: TripRouteStop, freights: TripRouteFreight[]) {
  if (!tripManager.value) return;

  tripManager.value.updateRouteStop(stop);

  nextTick(() => {
    tripManager.value?.updateFreights(routeItem, freights, stop);
  });

  nextTick(() => {
    tripManager.value?.addReloadFreights(stop);
  });

  nextTick(() => {
    tripManager.value?.updateTravelOrderTempIds();
  });
}

function onTravelOrderUpdate(routeItem: TripRoute, travelOrder: TravelOrderDto) {
  if (!tripManager.value) return;

  tripManager.value.updateRouteStatus(routeItem);

  nextTick(() => {
    tripManager.value?.editTravelOrder(travelOrder);
  });

  nextTick(() => {
    tripManager.value?.editOrderByTravelOrder(travelOrder);
  });
}

function showReorderStopsDialog() {
  isReorderStopDialog.value = true;
}

function reorderTripStop(tripStops: TripStop[]) {
  tripManager.value?.reorderTripStops(tripStops);
}

onMounted(async () => {
  loading.value = true;
  const tripManagerInstance = new TripManager(api, route);
  tripManager.value = tripManagerInstance;

  tripGrid.value = new TripGrid(tripManagerInstance);
  await tripManager.value.load();

  if (tripManager.value.shouldShowReorderStopsDialog) {
    showReorderStopsDialog();
  }

  tripFromAPI.value = { ...tripManager.value.trip };
  loading.value = false;
});
</script>

<template>
  <v-container :fluid="true" class="pt-8 pb-10 pl-6 pr-6">
    <template v-if="!loading && tripManager">
      <div class="pb-15">
        <div :style="gridStyle" class="stops-grid">
          <trip-info
            :orders="tripManager.trip.orders"
            @add:order="addOrderDialogVisible = true"
            @add:route="addEmptyRoute"
            @edit:stops-order="showReorderStopsDialog"
          ></trip-info>
          <order-block
            v-for="(order, index) in trip.orders"
            :key="order.id"
            :can-remove="trip.orders.length > 1"
            :index="index"
            :order="order"
            class="mb-4"
            @remove="removeOrder"
          />
          <div
            :style="stopsContainerStyle"
            class="trips-block bg-white pa-4 pt-0 rounded-lg position-relative rounded-tl-0 rounded-tr-0 rounded-bl-0 rounded-br-0"
          >
            <div class="trip-block__header d-flex align-center justify-space-between my-2">
              <span class="text-grey-darken-2 font-weight-medium">Trip stops</span>
            </div>
            <template v-for="(stop, i) in sortedTripStops" :key="stop.id">
              <trip-stop-block
                :order-freights="orderFreights"
                :orders="trip.orders"
                :routes="trip.routes"
                :stop="stop"
              />

              <div v-if="i < sortedTripStops.length - 1" class="plus-block text-center">
                <v-btn
                  class="text-primary plus-btn"
                  icon="mdi-plus"
                  size="small"
                  variant="flat"
                  @click="showReloadStopDialog(i)"
                />
              </div>
            </template>
          </div>
          <template v-for="(routeItem, i) in trip.routes" :key="i">
            <route-block
              v-model="trip.routes[i]"
              :can-remove="trip.routes.length > 1"
              :orders="trip.orders"
              :trip-stops="trip.tripStops"
              class="mb-4"
              @attach="(truck) => setTruck(trip.routes[i], truck)"
              @remove-truck="setTruck(trip.routes[i], null)"
              @remove-route="trip.routes.splice(i, 1)"
            />

            <template v-for="(travelOrder, index) in routeItem.travelOrders" :key="travelOrder.tempId">
              <travel-order-block
                v-if="routeItem.truck"
                v-model="routeItem.travelOrders[index]"
                :route="routeItem"
                :route-stops="routeItem.routeStops"
                :routes="trip.routes"
                :travel-orders-by-order="travelOrdersByOrder"
                :trip-from-api="tripFromAPI"
                :truck="routeItem.truck"
                class="mb-4"
                @update:model-value="(to) => onTravelOrderUpdate(routeItem, to)"
              />
            </template>

            <div
              v-if="routeItem.routeStops.length > 0"
              :style="stopsContainerStyle"
              class="trip-block bg-white pa-4 rounded-lg position-relative rounded-tl-0 rounded-tr-0 rounded-bl-0 rounded-br-0"
              data-qa="route-stops"
            >
              <div class="trip-block__header d-flex align-center justify-space-between mb-3">
                <span class="text-grey-darken-2 font-weight-medium">Route stops</span>
              </div>
              <template v-for="(routeStop, index) in routeItem.routeStops" :key="index">
                <route-stop-block
                  :allow-edit-usage="trip.routes.length > 1"
                  :order-freights="orderFreights"
                  :route="routeItem"
                  :routes="trip.routes"
                  :stop="routeStop"
                  :trip-from-api="tripFromAPI"
                  :trip-stops="trip.tripStops"
                  @changed:route="
                    (stop: TripRouteStop, freights: TripRouteFreight[]) => changeRoute(routeItem, stop, freights)
                  "
                />

                <div v-if="index < trip.tripStops.length - 1"></div>
              </template>
            </div>
          </template>
        </div>

        <div class="d-flex justify-end pa-5 bg-white user-info__buttons">
          <v-btn
            v-if="cancelEnabled"
            id="cancel-btn"
            class="pr-6 pl-6 font-weight-600 text-uppercase"
            color="primary"
            height="36"
            variant="text"
            @click="() => toView()"
          >
            Cancel
          </v-btn>

          <tooltip-button :rules="saveAndDispatchRules">
            <template #default="{ disabled }">
              <v-btn
                id="save-btn"
                :disabled="disabled"
                :loading="saveLoading"
                class="pr-6 pl-6 font-weight-600 text-uppercase"
                color="primary"
                height="36"
                variant="flat"
                @click="() => save()"
              >
                <template v-if="disabled">Save</template>
                <template v-else>Save and dispatch</template>
              </v-btn>
            </template>
          </tooltip-button>
        </div>
      </div>

      <add-order-dialog
        v-if="addOrderDialogVisible"
        v-model="addOrderDialogVisible"
        :orders="trip.orders"
        :trip-manager="tripManager"
        :trip-stops="trip.tripStops"
        @change="addOrder"
      />

      <add-reload-stop-dialog
        v-if="addReloadStopDialogVisible"
        v-model="addReloadStopDialogVisible"
        :position="reloadStopPosition"
        :trip-stops="trip.tripStops"
        @add="addReloadStop"
      />

      <reorder-trip-stops-dialog
        v-if="isReorderStopDialog"
        v-model="isReorderStopDialog"
        :new-order="newAddedOrder"
        :trip="trip"
        @reorder="reorderTripStop"
      />
    </template>
  </v-container>
</template>

<style lang="scss" scoped>
.stops-grid {
  display: grid;
  grid-auto-flow: column;
  column-gap: 24px;
  overflow-x: auto;
  width: 100%;
}

.trip-stops__block-inner {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.plus-block {
  line-height: 1;
}

.reload-stop-list {
  display: flex;
  flex-direction: column;
}
</style>
